import { Component, ElementRef, forwardRef, Input, ViewChild, AfterViewInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-rich-text-editor',
  standalone: true,
  imports: [CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true
    }
  ],
  templateUrl: './rich-text-editor.html',
  styles: [`
    .rich-editor-content {
      position: relative;
    }
    .rich-editor-content:empty:before {
      content: attr(placeholder);
      position: absolute;
      color: #9ca3af;
      pointer-events: none;
    }
  `]
})
export class RichTextEditorComponent implements ControlValueAccessor, AfterViewInit {
  @Input() placeholder: string = 'Write something...';
  @Input() readonly: boolean = false;
  @Input() heightClass: string = 'min-h-[100px]';

  @ViewChild('editor', { static: true }) editor!: ElementRef<HTMLDivElement>;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  value: string = '';

  // ControlValueAccessor methods
  writeValue(value: string): void {
    this.value = value || '';
    if (this.editor && this.editor.nativeElement) {
      this.editor.nativeElement.innerHTML = this.value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.readonly = isDisabled;
  }

  ngAfterViewInit(): void {
    if (this.editor && this.editor.nativeElement) {
      this.editor.nativeElement.innerHTML = this.value || '';
    }
  }

  onInput(): void {
    const html = this.editor.nativeElement.innerHTML;
    // If it's just <br> or empty, set to empty string
    const cleanedHtml = (html === '<br>' || html === '<div><br></div>') ? '' : html;
    this.value = cleanedHtml;
    this.onChange(cleanedHtml);
  }

  onBlur(): void {
    this.onTouched();
  }

  onKeyDown(event: KeyboardEvent): void {
    // If they press Enter without shift in some contexts (e.g. chat), the parent can intercept it.
    // For general editing, Enter works naturally in contenteditable.
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain') || '';
    // Insert plain text to keep formatting clean
    document.execCommand('insertText', false, text);
  }

  format(command: string, value: string = ''): void {
    if (this.readonly) return;
    document.execCommand(command, false, value);
    this.onInput();
    this.editor.nativeElement.focus();
  }

  link(): void {
    if (this.readonly) return;
    const url = prompt('Enter the link URL:');
    if (url) {
      this.format('createLink', url);
    }
  }

  // Exposed utility helper for mentions
  getTextBeforeCursor(): { text: string; range: Range | null } {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return { text: '', range: null };
    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(this.editor.nativeElement);
    preCaretRange.setEnd(range.endContainer, range.endOffset);
    return { text: preCaretRange.toString(), range };
  }

  // Exposed utility helper to replace word before cursor (e.g. for mentions)
  replaceWordBeforeCursor(regex: RegExp, replacement: string): void {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    
    const node = range.endContainer;
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      const offset = range.endOffset;
      const textBefore = text.substring(0, offset);
      const match = textBefore.match(regex);
      if (match) {
        const matchIndex = match.index ?? 0;
        const matchedText = match[0];
        
        const beforeMatch = textBefore.substring(0, matchIndex);
        const textAfter = text.substring(offset);
        
        node.textContent = beforeMatch + replacement + textAfter;
        
        const newOffset = beforeMatch.length + replacement.length;
        const newRange = document.createRange();
        newRange.setStart(node, newOffset);
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);
        
        this.onInput();
      }
    }
  }

  focus(): void {
    if (this.editor) {
      this.editor.nativeElement.focus();
    }
  }
}
